/**
 * GedcomValidationSummary Component
 *
 * Displays validation results for GEDCOM preview.
 * Shows readiness status, summary counts, and detailed issues list.
 *
 * Part of Phase 4 - Slice 5: Validation Framework
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { ValidationResult } from '../types/GedcomValidation';

interface GedcomValidationSummaryProps {
  validation: ValidationResult;
}

export function GedcomValidationSummary({ validation }: GedcomValidationSummaryProps) {
  const { t } = useTranslation();

  // Get status badge
  const getStatusBadge = () => {
    switch (validation.status) {
      case 'ready':
        return (
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
            <CheckCircle className="size-5" />
            <span className="font-medium">{t('gedcom.validation.ready', 'Ready to Import')}</span>
          </div>
        );
      case 'ready_with_warnings':
        return (
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="size-5" />
            <span className="font-medium">{t('gedcom.validation.readyWithWarnings', 'Ready with Warnings')}</span>
          </div>
        );
      case 'blocked':
        return (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="size-5" />
            <span className="font-medium">{t('gedcom.validation.blocked', 'Import Blocked')}</span>
          </div>
        );
    }
  };

  const getSeverityIcon = (severity: 'error' | 'warning' | 'info') => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="size-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="size-4 text-amber-500" />;
      case 'info':
        return <Info className="size-4 text-blue-500" />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">{t('gedcom.validation.title', 'Validation Summary')}</h3>
          {getStatusBadge()}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-slate-500 dark:text-slate-400">{t('gedcom.validation.individuals', 'Individuals')}</div>
            <div className="text-lg font-semibold">{validation.summary.totalIndividuals}</div>
          </div>
          <div>
            <div className="text-slate-500 dark:text-slate-400">{t('gedcom.validation.families', 'Families')}</div>
            <div className="text-lg font-semibold">{validation.summary.totalFamilies}</div>
          </div>
          <div>
            <div className="text-slate-500 dark:text-slate-400">{t('gedcom.validation.warningsLabel', 'Warnings')}</div>
            <div className="text-lg font-semibold text-amber-600">{validation.summary.warningCount}</div>
          </div>
          <div>
            <div className="text-slate-500 dark:text-slate-400">{t('gedcom.validation.errorsLabel', 'Errors')}</div>
            <div className="text-lg font-semibold text-red-600">{validation.summary.errorCount}</div>
          </div>
        </div>

        {/* Blocked message */}
        {validation.status === 'blocked' && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-800 dark:text-red-200">
            {t('gedcom.validation.blockedMessage', 'Import is blocked due to critical errors. Please fix the issues before importing.')}
          </div>
        )}
      </div>

      {/* Issues List */}
      {validation.issues.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium text-sm">{t('gedcom.validation.issues', 'Issues Found')}:</h4>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {validation.issues.map((issue, index) => (
              <div
                key={index}
                className={`flex items-start gap-2 p-3 rounded border text-sm ${
                  issue.severity === 'error'
                    ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                    : issue.severity === 'warning'
                    ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800'
                    : 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'
                }`}
              >
                <div className="mt-0.5">{getSeverityIcon(issue.severity)}</div>
                <div className="flex-1">
                  <div className={`font-medium ${
                    issue.severity === 'error' ? 'text-red-800 dark:text-red-200' :
                    issue.severity === 'warning' ? 'text-amber-800 dark:text-amber-200' :
                    'text-blue-800 dark:text-blue-200'
                  }`}>
                    {issue.message}
                  </div>
                  {issue.gedcomId && (
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      ID: {issue.gedcomId}
                      {issue.relatedIds && issue.relatedIds.length > 0 && (
                        <span> (Related: {issue.relatedIds.join(', ')})</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
